import '@uiw/react-md-editor/markdown-editor.css';

import { PropsOf } from '@headlessui/react/dist/types';
import { PhotoIcon } from '@heroicons/react/24/solid';
import MDEditor, { getCommands } from '@uiw/react-md-editor';
import React, { useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';

import { useTheme } from '@/app/ThemeContext';
import {
  AppMediaStoragePrefix,
  MarkdownPreview,
} from '@/components/markdown/MarkdownPreview';
import { useUploadFile } from '@/hooks/useFiles';
import { cn } from '@/utils/classname';
import { generateUuid } from '@/utils/uuid.util';

export function MarkdownEditor(
  props: Omit<PropsOf<typeof MDEditor>, 'components' | 'onDrop'> & {
    filePathPrefix?: string;
    email?: string;
  }
) {
  const { currentTheme } = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const { filePathPrefix = 'user-attachment' } = props;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { mutateAsync: updateFile } = useUploadFile(props.email);

  // Use props directly - standard controlled component pattern
  // This eliminates the infinite loop caused by useStorage's bidirectional sync
  const markdown = props.value ?? '';

  const commands = getCommands();

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileUpload,
  });

  const customCommands = commands.flatMap(command => {
    if (command.keyCommand === 'image') {
      return [
        command,
        {
          name: 'addImage',
          keyCommand: 'addImage',
          render: () => (
            <button className="p-1" {...getRootProps()}>
              <input {...getInputProps()} />
              <PhotoIcon className="-mt-px size-[14px] scale-110" />
            </button>
          ),
          shortcuts: 'ctrlcmd+k',
          prefix: '![image](',
          suffix: ')',
          buttonProps: {
            title: 'Click to Add image',
          },
        },
      ];
    }

    return command;
  });

  async function handleFileUpload(files: File[]) {
    const uploadingImages = files
      .map((file): UploadingImage | undefined => {
        if (file) {
          if (/image\/.*/.test(file.type)) {
            const id = generateUuid();
            const rawExt = file.name.includes('.')
              ? (file.name.split('.').pop() ?? '')
              : '';
            const ext = /^[a-zA-Z0-9]+$/.test(rawExt) ? `.${rawExt}` : '';
            const src = `${filePathPrefix}/${id}${ext}`;

            return {
              markdownText: `![${file.name}](${AppMediaStoragePrefix}${src})`,
              mutate: async () => {
                await updateFile({
                  ignoreSnackbar: true,
                  name: src,
                  content: await file.arrayBuffer(),
                });
              },
            };
          }
        }
      })
      .filter(x => x) as UploadingImage[];

    if (uploadingImages.length > 0) {
      const uploadingImagesPromise = uploadingImages.map(i => i.mutate());
      const uploadingImagesText = uploadingImages
        .map(i => i.markdownText)
        .join('\n');

      const uploadingId = generateUuid();
      const uploadingText = `Uploading ${uploadingId}`;

      const textarea = (
        textareaRef.current as unknown as { textarea: HTMLTextAreaElement }
      )?.textarea as HTMLTextAreaElement;

      if (textarea) {
        const currentText = textarea.value || '';
        const cursorPosition = textarea.selectionStart || 0;

        // Insert the text at the cursor position
        const newText =
          currentText.slice(0, cursorPosition) +
          uploadingText +
          currentText.slice(cursorPosition);

        // Update the textarea with the new text via parent onChange
        props.onChange?.(newText);

        // Move the cursor to the end of the inserted text
        textarea.selectionStart = textarea.selectionEnd =
          cursorPosition + uploadingText.length;

        try {
          await Promise.all(uploadingImagesPromise);

          // Replace placeholder with actual images via parent onChange
          // Use newText instead of props.value to avoid race condition
          // (props.value may not have updated yet with the placeholder)
          props.onChange?.(newText.replace(uploadingText, uploadingImagesText));
        } catch {
          // Remove placeholder on failure so the user isn't left with dead text
          props.onChange?.(newText.replace(uploadingText, ''));
        }
      }
    }
  }

  async function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    if (event.dataTransfer.files.length > 0) {
      event.preventDefault();

      const files = Array(event.dataTransfer.items.length)
        .fill(0)
        .map((_, index): File | null => {
          return event.dataTransfer.files.item(index);
        })
        .filter(x => x) as File[];

      handleFileUpload(files);
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLDivElement>) {
    const items = event.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    if (imageFiles.length > 0) {
      event.preventDefault();
      handleFileUpload(imageFiles);
    }
  }

  useEffect(() => {
    // Force theme update
    if (editorRef.current) {
      const mdEditor = editorRef.current;
      const colorMode = currentTheme === 'dark' ? 'dark' : 'light';

      // Update data-color-mode attribute
      mdEditor.setAttribute('data-color-mode', colorMode);

      // Update all child elements with the same attribute
      mdEditor
        .querySelectorAll('[data-color-mode]')
        .forEach((element: HTMLElement) => {
          element.setAttribute('data-color-mode', colorMode);
        });
    }
  }, [currentTheme]);

  // Extract custom props before spreading to avoid React DOM warnings
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const {
    filePathPrefix: _unused,
    email: _unusedEmail,
    ...mdEditorProps
  } = props;
  /* eslint-enable @typescript-eslint/no-unused-vars */

  return (
    <div
      className="size-full"
      ref={editorRef}
      data-color-mode={currentTheme}
      onPaste={handlePaste}
    >
      <MDEditor
        {...mdEditorProps}
        ref={textareaRef}
        value={markdown}
        height="100%"
        onChange={value => {
          // Call parent onChange directly - standard controlled component pattern
          props.onChange?.(value || '');
        }}
        highlightEnable={false}
        className={cn(
          'markdownSelection border-chariot-stroke text-chariot-text-primary overflow-hidden rounded-lg border bg-transparent shadow-none',
          '[&_.w-md-editor-toolbar]:border-chariot-stroke [&_.w-md-editor-toolbar]:bg-transparent',
          '[&_.w-md-editor-preview]:border-chariot-stroke [&_.w-md-editor-preview]:bg-chariot-background-primary [&_.w-md-editor-preview]:border-l [&_.w-md-editor-preview]:shadow-none',
          props.className
        )}
        components={{
          preview: source => {
            return <MarkdownPreview source={source} email={props.email} />;
          },
        }}
        commandsFilter={command =>
          command.keyCommand !== 'image' ? command : false
        }
        commands={customCommands}
        onDrop={handleDrop}
      />
    </div>
  );
}

interface UploadingImage {
  markdownText: string;
  mutate: () => Promise<void>;
}
